/**
 * ColumnMenu component for per-column filtering and sorting
 * Based on column sort/filter system documentation
 */
import React, { useState, useMemo } from 'react';
import {
  Button, Popover, Radio, Input, InputNumber, Select, Checkbox,
  DatePicker, Switch, Space, Divider, Alert, Typography
} from 'antd';
import {
  FilterOutlined, SortAscendingOutlined, SortDescendingOutlined,
  CloseCircleFilled
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Filter operators
const OPERATORS = {
  EQ: 'eq',
  REGEX: 'regex',
  BETWEEN: 'between',
  IN: 'in',
  IS_EMPTY: 'is_empty',
};

/**
 * ColumnMenu - Popover with sorting and filtering for table columns
 * 
 * @param {Object} props
 * @param {string} props.columnId - Column ID (slug)
 * @param {string} props.label - Display label
 * @param {string} props.fieldType - Type: text, number, date, boolean, single_choice, multi_choice
 * @param {Array} props.options - Options for choice types
 * @param {Object} props.rule - Current active filter rule
 * @param {string} props.sort - Current sort value
 * @param {Function} props.onSortChange - Sort change handler
 * @param {Function} props.onApply - Apply filter handler (slug, operator, value)
 * @param {Function} props.onClear - Clear filter handler
 * @param {boolean} props.disableSort - Disable sorting for this column
 */
function ColumnMenu({
  columnId,
  label,
  fieldType = 'text',
  options = [],
  rule,
  sort,
  onSortChange,
  onApply,
  onClear,
  disableSort = false,
}) {
  const [open, setOpen] = useState(false);
  const [tempOperator, setTempOperator] = useState(rule?.operator || getDefaultOperator(fieldType));
  const [tempValue, setTempValue] = useState(rule?.value || '');

  // Reset temp values when popover opens
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (newOpen) {
      setTempOperator(rule?.operator || getDefaultOperator(fieldType));
      setTempValue(rule?.value || '');
    }
  };

  // Check if this column has an active filter
  const hasActiveFilter = rule && rule.value != null && rule.value !== '';
  
  // Check if this column is sorted
  const isSorted = sort === columnId || sort === `-${columnId}`;
  const isAsc = sort === columnId;
  const isDesc = sort === `-${columnId}`;

  // Handle sort button click
  const handleSort = (direction) => {
    if (direction === 'asc') {
      onSortChange(columnId);
    } else {
      onSortChange(`-${columnId}`);
    }
  };

  // Handle apply filter
  const handleApply = () => {
    if (tempValue || tempOperator === OPERATORS.IS_EMPTY) {
      onApply(columnId, tempOperator, tempValue);
    }
    setOpen(false);
  };

  // Handle clear filter
  const handleClear = () => {
    onClear(columnId);
    setTempValue('');
    setTempOperator(getDefaultOperator(fieldType));
    setOpen(false);
  };

  // Render filter input based on field type
  const renderFilterInput = () => {
    switch (fieldType) {
      case 'text':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Switch
                size="small"
                checked={tempOperator === OPERATORS.REGEX}
                onChange={(checked) => setTempOperator(checked ? OPERATORS.REGEX : OPERATORS.EQ)}
              />
              <Text type="secondary">Regex</Text>
            </Space>
            <Input
              placeholder={tempOperator === OPERATORS.REGEX ? "Регулярное выражение" : "Поиск..."}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onPressEnter={handleApply}
              allowClear
            />
          </Space>
        );

      case 'number':
        const [minVal = '', maxVal = ''] = tempValue ? tempValue.split('|') : ['', ''];
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <InputNumber
              placeholder="От"
              value={minVal || undefined}
              onChange={(val) => setTempValue(`${val || ''}|${maxVal}`)}
              style={{ width: '100%' }}
            />
            <InputNumber
              placeholder="До"
              value={maxVal || undefined}
              onChange={(val) => setTempValue(`${minVal}|${val || ''}`)}
              style={{ width: '100%' }}
            />
          </Space>
        );

      case 'date':
        const [startDate = '', endDate = ''] = tempValue ? tempValue.split('|') : ['', ''];
        return (
          <RangePicker
            style={{ width: '100%' }}
            value={startDate ? [dayjs(startDate), endDate ? dayjs(endDate) : null] : [null, null]}
            onChange={(dates) => {
              const start = dates?.[0]?.format('YYYY-MM-DD') || '';
              const end = dates?.[1]?.format('YYYY-MM-DD') || '';
              setTempValue(`${start}|${end}`);
            }}
          />
        );

      case 'boolean':
        return (
          <Select
            style={{ width: '100%' }}
            value={tempValue}
            onChange={(val) => setTempValue(val)}
            placeholder="Выберите..."
            allowClear
            options={[
              { label: 'Да', value: 'true' },
              { label: 'Нет', value: 'false' },
            ]}
          />
        );

      case 'single_choice':
      case 'multi_choice':
        return (
          <Checkbox.Group
            style={{ width: '100%' }}
            value={tempValue ? tempValue.split(',').filter(Boolean) : []}
            onChange={(checkedValues) => setTempValue(checkedValues.join(','))}
          >
            <Space direction="vertical">
              {options.map((opt) => (
                <Checkbox key={opt} value={opt}>{opt}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      default:
        return (
          <Input
            placeholder="Поиск..."
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onPressEnter={handleApply}
            allowClear
          />
        );
    }
  };

  // Popover content
  const content = (
    <div style={{ width: 280 }}>
      {/* Active filter indicator */}
      {hasActiveFilter && (
        <>
          <Alert
            type="info"
            message={
              <Text ellipsis style={{ maxWidth: 240 }}>
                Фильтр: {formatFilterValue(rule)}
              </Text>
            }
            style={{ marginBottom: 12 }}
          />
        </>
      )}

      {/* Sort section */}
      {!disableSort && (
        <>
          <Text strong>Сортировка</Text>
          <div style={{ margin: '8px 0' }}>
            <Space>
              <Button
                size="small"
                type={isAsc ? 'primary' : 'default'}
                icon={<SortAscendingOutlined />}
                onClick={() => handleSort('asc')}
              >
                По возрастанию
              </Button>
              <Button
                size="small"
                type={isDesc ? 'primary' : 'default'}
                icon={<SortDescendingOutlined />}
                onClick={() => handleSort('desc')}
              >
                По убыванию
              </Button>
            </Space>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {/* Filter section */}
      <Text strong>Фильтр</Text>
      <div style={{ margin: '8px 0' }}>
        {renderFilterInput()}
      </div>

      {/* Action buttons */}
      <Divider style={{ margin: '12px 0' }} />
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={handleClear} disabled={!hasActiveFilter}>
          Сбросить
        </Button>
        <Button size="small" type="primary" onClick={handleApply}>
          Применить
        </Button>
      </Space>
    </div>
  );

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Text>{label}</Text>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomLeft"
      >
        <Button
          type={hasActiveFilter ? 'primary' : 'text'}
          size="small"
          icon={<FilterOutlined />}
          style={{ 
            padding: '0 4px',
            minWidth: 24,
            height: 24,
          }}
        />
      </Popover>
    </div>
  );
}

// Helper: Get default operator for field type
function getDefaultOperator(fieldType) {
  switch (fieldType) {
    case 'text':
      return OPERATORS.EQ;
    case 'number':
    case 'date':
      return OPERATORS.BETWEEN;
    case 'boolean':
      return OPERATORS.EQ;
    case 'single_choice':
    case 'multi_choice':
      return OPERATORS.IN;
    default:
      return OPERATORS.EQ;
  }
}

// Helper: Format filter value for display
function formatFilterValue(rule) {
  if (!rule) return '';
  
  const { operator, value } = rule;
  
  switch (operator) {
    case OPERATORS.EQ:
      return value;
    case OPERATORS.REGEX:
      return `/${value}/`;
    case OPERATORS.BETWEEN:
      const [min, max] = value.split('|');
      if (min && max) return `${min} — ${max}`;
      if (min) return `от ${min}`;
      if (max) return `до ${max}`;
      return '';
    case OPERATORS.IN:
      return value.split(',').join(', ');
    case OPERATORS.IS_EMPTY:
      return 'пусто';
    default:
      return value;
  }
}

export default ColumnMenu;